import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createClient, type SupabaseClient, type User, type Session } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';

import AuthLoadingFullscreen from '../components/auth/AuthLoadingFullscreen';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  authUser: User | null;
  session: Session;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session>();
  const [loading, setLoading] = useState(true);
  const [supabase, setSupabase] = useState<SupabaseClient<Database> | null>(null);
  const navigate = useNavigate();
  const isLocalhost: boolean = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | undefined;

    const init = async () => {
      let response;
      if(isLocalhost){
        const api_addr = 'https://preprod.crm.rivieraapps.com';
        response = await fetch(`${api_addr}/api/auth/supabase-credentials`);
      }else{
        response = await fetch('/api/auth/supabase-credentials');
      }
      const data = await response.json();

      if (!data.url || !data.key) {
        console.error('Missing Supabase credentials');
        signOut();
      }

      const client = createClient<Database>(
        data.url,
        data.key,
      );

      setSupabase(client);

      const { data: { session }, error } = await client.auth.getSession();
      if (session) {
        const { data: { user } } = await client.auth.getUser();
        setAuthUser(user);
        setSession(session);
        // store to localStorage for later use in getAuthHeaders
        localStorage.setItem('session', JSON.stringify(session));
        localStorage.setItem('authUser', JSON.stringify(user));
        localStorage.setItem('authHeadersTimestamp', new Date().toISOString());
      } else {
        console.error('no session could be created,  signing out');
        console.log("Session:", session, "error: ", error);
      }

      setLoading(false);

      const { data: authListener } = client.auth.onAuthStateChange((_event, nextSession) => {
        if (nextSession) {
          setSession(nextSession);
          setAuthUser(nextSession?.user ?? null);
        } else {
          console.log('Auth state changed, user signed out');
          setAuthUser(null);
        }
      });
      subscription = authListener.subscription;
    };

    init().catch((error) => {
      console.error('Error initializing Supabase:', error);
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const requireClient = () => {
    if (!supabase) throw new Error('Supabase client not initialized yet');
    return supabase;
  };

  const signOut = async () => {
    const { error } = await requireClient().auth.signOut();
    if (error) throw new Error(error.message);
    setAuthUser(null);
    setSession(undefined);
    localStorage.removeItem('session');
    localStorage.removeItem('authUser');
    localStorage.removeItem('authHeadersTimestamp');
    navigate('/');
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await requireClient().auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { data, error } = await requireClient().auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
      },
    });

    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Signup failed');
  };

  const signInWithGoogle = async () => {
    const { error } = await requireClient().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });

    if (error) throw new Error(error.message);
  };

  return (
    <AuthContext.Provider
      value={{
        authUser,
        session,
        loading,
        signOut,
        signIn,
        signUp,
        signInWithGoogle,
      }}
    >
      <AuthLoadingFullscreen isOpen={loading} />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
